import { useQuery } from '@tanstack/react-query';

import React, { useMemo, useState } from 'react';

import { AccountTree, AddCircle, RadioButtonChecked, RadioButtonUnchecked, Search } from '@mui/icons-material';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	InputAdornment,
	ListItemButton,
	ListItemText,
	Stack,
	TextField,
	Typography,
} from '@mui/material';

import { useApiClient } from '@@common';

import { ContextClient, IntegrationEventModel } from '../../../api/api';
import { contextDesignerQueryKeys } from '../../ContextDesigner/ContextDesignerQueryKeys';

interface Props {
	onCreateHandler: (integrationEventId: string, name?: string, description?: string) => Promise<unknown>;

	/** New preferred prop name */
	integrationEventModels?: IntegrationEventModel[];

	/** Back-compat: will be ignored if `integrationEventModels` is provided */
	availableEvents?: IntegrationEventModel[];
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	hideButton?: boolean;
}

interface EventContextGroup {
	id: string;
	name: string;
	events: IntegrationEventModel[];
}

interface EventSubDomainGroup {
	id: string;
	name: string;
	contexts: EventContextGroup[];
}

const CreateEventHandlerButton: React.FC<Props> = ({ onCreateHandler, integrationEventModels, availableEvents, open, onOpenChange, hideButton = false }) => {
	const contextClient = useApiClient(ContextClient);
	const [dialogOpen, setDialogOpen] = useState(false);
	const isDialogOpen = open ?? dialogOpen;
	const setIsDialogOpen = (next: boolean) => {
		setDialogOpen(next);
		onOpenChange?.(next);
	};
	const [integrationEventId, setIntegrationEventId] = useState('');
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [searchText, setSearchText] = useState('');
	const { data: subDomains = [] } = useQuery({
		queryKey: contextDesignerQueryKeys.subDomains(),
		queryFn: () => contextClient.getSubDomains(),
		staleTime: 5 * 60 * 1000,
	});

	// Prefer integrationEventModels; fallback to availableEvents for backward compatibility
	const events = useMemo(() => (integrationEventModels ?? availableEvents ?? []).filter(Boolean), [integrationEventModels, availableEvents]);
	const eventGroups = useMemo<EventSubDomainGroup[]>(() => {
		const contextLocations = new Map<string, { subDomainId: string; subDomainName: string; contextName: string }>();
		for (const item of subDomains) {
			for (const context of item.contexts ?? []) {
				contextLocations.set(context.id, {
					subDomainId: item.subDomain.id,
					subDomainName: item.subDomain.name,
					contextName: context.contextName,
				});
			}
		}

		const normalizedSearch = searchText.trim().toLowerCase();
		const grouped = new Map<string, EventSubDomainGroup>();
		for (const integrationEvent of events) {
			const location = contextLocations.get(integrationEvent.contextId);
			const subDomainId = location?.subDomainId ?? 'unknown-subdomain';
			const subDomainName = location?.subDomainName ?? 'Other Subdomain';
			const contextName = location?.contextName ?? 'Unknown Context';
			const searchableText = [integrationEvent.name, integrationEvent.description, contextName, subDomainName, ...(integrationEvent.tags ?? [])]
				.join(' ')
				.toLowerCase();
			if (normalizedSearch && !searchableText.includes(normalizedSearch)) {
				continue;
			}

			let subDomainGroup = grouped.get(subDomainId);
			if (!subDomainGroup) {
				subDomainGroup = { id: subDomainId, name: subDomainName, contexts: [] };
				grouped.set(subDomainId, subDomainGroup);
			}

			let contextGroup = subDomainGroup.contexts.find((item) => item.id === integrationEvent.contextId);
			if (!contextGroup) {
				contextGroup = { id: integrationEvent.contextId, name: contextName, events: [] };
				subDomainGroup.contexts.push(contextGroup);
			}
			contextGroup.events.push(integrationEvent);
		}

		return Array.from(grouped.values())
			.sort((left, right) => left.name.localeCompare(right.name))
			.map((subDomain) => ({
				...subDomain,
				contexts: subDomain.contexts
					.sort((left, right) => left.name.localeCompare(right.name))
					.map((context) => ({ ...context, events: context.events.sort((left, right) => left.name.localeCompare(right.name)) })),
			}));
	}, [events, searchText, subDomains]);

	const handleSave = async () => {
		if (!integrationEventId) return;
		await onCreateHandler(integrationEventId, name.trim() || undefined, description.trim() || undefined);
		setIsDialogOpen(false);
		setIntegrationEventId('');
		setName('');
		setDescription('');
		setSearchText('');
	};

	const noEvents = events.length === 0;

	return (
		<>
			{!hideButton && (
				<Box sx={{ mb: 2 }}>
					<ListItemButton onClick={() => setIsDialogOpen(true)} sx={{ pl: 2 }} disabled={noEvents}>
						<AddCircle fontSize="small" sx={{ mr: 1 }} />
						<ListItemText
							primary={<Typography variant="body1">Create Integration Event Handler</Typography>}
							secondary={noEvents ? 'No integration events available' : undefined}
						/>
					</ListItemButton>
				</Box>
			)}

			<Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)} fullWidth maxWidth="md">
				<DialogTitle>Create Integration Event Handler</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ mt: 1 }}>
						<TextField
							autoFocus
							placeholder="Search integration events"
							value={searchText}
							onChange={(event) => setSearchText(event.target.value)}
							disabled={noEvents}
							fullWidth
							slotProps={{
								input: {
									startAdornment: (
										<InputAdornment position="start">
											<Search fontSize="small" />
										</InputAdornment>
									),
								},
							}}
						/>
						<Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
							{noEvents ? (
								<Alert severity="info">No integration events are available to handle.</Alert>
							) : eventGroups.length === 0 ? (
								<Alert severity="info">No integration events match your search.</Alert>
							) : (
								<Stack spacing={2.5}>
									{eventGroups.map((subDomain) => (
										<Box key={subDomain.id}>
											<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
												<AccountTree fontSize="small" color="action" />
												<Typography variant="subtitle2">{subDomain.name}</Typography>
											</Stack>
											<Stack spacing={1.5} sx={{ pl: { xs: 0, sm: 3 } }}>
												{subDomain.contexts.map((context) => (
													<Box key={context.id}>
														<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
															{context.name}
														</Typography>
														<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 250px), 1fr))', gap: 1 }}>
															{context.events.map((integrationEvent) => {
																const selected = integrationEvent.id === integrationEventId;
																return (
																	<ListItemButton
																		key={integrationEvent.id}
																		selected={selected}
																		onClick={() => {
																			setIntegrationEventId(integrationEvent.id);
																			setName(`On${integrationEvent.name}`);
																		}}
																		sx={{
																			alignItems: 'flex-start',
																			border: 1,
																			borderColor: selected ? 'primary.main' : 'divider',
																			borderRadius: 1,
																			minHeight: 76,
																		}}
																	>
																		{selected ? (
																			<RadioButtonChecked color="primary" sx={{ mr: 1, mt: 0.25 }} />
																		) : (
																			<RadioButtonUnchecked color="action" sx={{ mr: 1, mt: 0.25 }} />
																		)}
																		<ListItemText
																			primary={integrationEvent.name}
																			secondary={integrationEvent.description || 'No description'}
																			primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
																			secondaryTypographyProps={{ variant: 'caption', sx: { mt: 0.5 } }}
																		/>
																	</ListItemButton>
																);
															})}
														</Box>
													</Box>
												))}
											</Stack>
										</Box>
									))}
								</Stack>
							)}
						</Box>
					</Stack>
					<TextField
						label="Integration Event Handler Name"
						value={name}
						onChange={(event) => setName(event.target.value)}
						placeholder="Defaults to the integration event name"
						fullWidth
						sx={{ mt: 2 }}
					/>
					<TextField
						label="Description"
						value={description}
						onChange={(event) => setDescription(event.target.value)}
						multiline
						minRows={3}
						fullWidth
						sx={{ mt: 2 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setIsDialogOpen(false)}>Cancel</Button>
					<Button onClick={handleSave} variant="contained" disabled={!integrationEventId || noEvents}>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default CreateEventHandlerButton;
